import React, { useState } from 'react';
import { supabase } from '../supabaseClient';
import { useNavigate } from 'react-router-dom';
import Button from './ui/Button';
import { Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';

const ResetPassword: React.FC = () => {
	const navigate = useNavigate();
	const [newPassword, setNewPassword] = useState('');
	const [confirmPassword, setConfirmPassword] = useState('');
	const [isLoading, setIsLoading] = useState(false);
	const [message, setMessage] = useState('');
	const [showNew, setShowNew] = useState(false);
	const [showConfirm, setShowConfirm] = useState(false);

	const handleReset = async (e: React.FormEvent) => {
		e.preventDefault();
		setMessage('');
		if (newPassword !== confirmPassword) {
			setMessage('Passwords do not match.');
			toast.error('Passwords do not match.');
			return;
		}
		setIsLoading(true);
		try {
			const { error } = await supabase.auth.updateUser({ password: newPassword });
			if (error) {
				setMessage(error.message);
				toast.error(error.message);
			} else {
				setMessage('Password reset successful! You can now log in with your new password.');
				toast.success('Password reset successful! You can now log in with your new password.');
				setTimeout(() => navigate('/admin'), 2000);
			}
		} catch (err) {
			setMessage('An unexpected error occurred. Please try again.');
			toast.error('An unexpected error occurred. Please try again.');
		} finally {
			setIsLoading(false);
		}
	};

	return (
		<div className="min-h-screen bg-slate-100 flex items-center justify-center p-2">
			<div className="w-full max-w-md">
				<div className="bg-white rounded-2xl shadow-xl p-6 py-8">
					<h1 className="text-xl font-bold text-gray-900 mb-2 text-center">Reset Password</h1>
					<p className="text-gray-500 text-sm mb-4 text-center">
						Enter your new password below to complete the reset process.
					</p>
					<form onSubmit={handleReset} className="space-y-6">
						<div>
							<label className="block text-xs font-medium text-gray-600 mb-1">New Password</label>
							<div className="relative">
								<input
									type={showNew ? 'text' : 'password'}
									value={newPassword}
									onChange={e => setNewPassword(e.target.value)}
									className="w-full px-3 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-ghana-red focus:border-transparent transition-colors pr-10"
									placeholder="Enter new password"
									required
								/>
								<button
									type="button"
									className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 cursor-pointer"
									onClick={() => setShowNew(v => !v)}
									tabIndex={-1}
								>
									{showNew ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
								</button>
							</div>
						</div>
						<div>
							<label className="block text-xs font-medium text-gray-600 mb-1">
								Confirm New Password
							</label>
							<div className="relative">
								<input
									type={showConfirm ? 'text' : 'password'}
									value={confirmPassword}
									onChange={e => setConfirmPassword(e.target.value)}
									className="w-full px-3 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-ghana-red focus:border-transparent transition-colors pr-10"
									placeholder="Confirm new password"
									required
								/>
								<button
									type="button"
									className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 cursor-pointer"
									onClick={() => setShowConfirm(v => !v)}
									tabIndex={-1}
								>
									{showConfirm ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
								</button>
							</div>
						</div>
						<Button
							type="submit"
							disabled={isLoading}
							className="w-full text-white font-semibold rounded-lg cursor-pointer hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
						>
							{isLoading ? 'Resetting...' : 'Reset Password'}
						</Button>
					</form>
					{message && <div className="mt-4 text-center text-sm text-ghana-red">{message}</div>}
					<div className="mt-4 text-center">
						<button
							onClick={() => navigate('/admin')}
							className="text-ghana-red hover:text-red-700 text-sm font-medium cursor-pointer"
						>
							← Back to Login
						</button>
					</div>
				</div>
			</div>
		</div>
	);
};

export default ResetPassword;
